/**
 * Centralizes cross-page sports asset grouping so bulk schedule editors can share
 * deterministic groups without depending on React or API state. Eligible assets
 * sort by title ascending, then by the canonical live-status declaration order;
 * unknown statuses follow declared statuses and the UNKNOWN bucket is last.
 */
import { LIVE_STATUSES, liveStatusRank } from 'src/constants';
import type { AssetDto } from 'src/store/generated/leela-api-generated';
import { assetStrategyKey } from './assetKeys';
import type { Schedule } from './computeScheduleWarnings';

export type { Schedule };

export interface BulkScheduleEventInfo {
  readonly predictedStartTime?: string | null;
  readonly predictedEndTime?: string | null;
}

interface MutableBulkScheduleGroup {
  readonly gti: string;
  readonly titleName: string;
  readonly liveStatus: string;
  readonly assetKeys: string[];
}

export interface BulkScheduleGroup {
  readonly groupId: string;
  readonly gti: string;
  readonly titleName: string;
  readonly liveStatus: string;
  readonly assetKeys: readonly string[];
  readonly currentSchedule: Schedule | 'mixed' | undefined;
  readonly eventStart?: string;
  readonly eventEnd?: string;
}

export function isBulkScheduleEligibleAsset (
  asset: AssetDto,
  sportsCampaignScheduleMap: ReadonlyMap<string, unknown>,
): boolean {
  return Boolean(
    asset.campaignId &&
      sportsCampaignScheduleMap.has(asset.campaignId) &&
      asset.titles?.length === 1 &&
      asset.titles[0]?.gti,
  );
}

function bulkScheduleGroupId (gti: string, liveStatus: string): string {
  return `${gti}::${liveStatus}`;
}

function getLiveStatusRank (liveStatus: string): number {
  return liveStatus === 'UNKNOWN'
    ? LIVE_STATUSES.length + 1
    : liveStatusRank(liveStatus);
}

function compareBulkScheduleGroups (
  left: BulkScheduleGroup,
  right: BulkScheduleGroup,
): number {
  return left.titleName.localeCompare(right.titleName) ||
    getLiveStatusRank(left.liveStatus) - getLiveStatusRank(right.liveStatus) ||
    left.liveStatus.localeCompare(right.liveStatus);
}

function getCurrentSchedule (
  assetKeys: readonly string[],
  assetSchedules: ReadonlyMap<string, Schedule>,
): Schedule | 'mixed' | undefined {
  const schedules = assetKeys.map((key) => assetSchedules.get(key));
  const stagedSchedules = schedules.filter(
    (schedule): schedule is Schedule => schedule !== undefined,
  );

  if (stagedSchedules.length === 0) return undefined;
  if (stagedSchedules.length !== assetKeys.length) return 'mixed';

  const firstSchedule = stagedSchedules[0];
  const isUniform = stagedSchedules.every(
    (schedule) =>
      schedule.startTime === firstSchedule.startTime &&
      schedule.endTime === firstSchedule.endTime,
  );
  return isUniform ? { ...firstSchedule } : 'mixed';
}

function createGroup (
  group: MutableBulkScheduleGroup,
  assetSchedules: ReadonlyMap<string, Schedule>,
  eventInfoByGti: ReadonlyMap<string, BulkScheduleEventInfo>,
): BulkScheduleGroup {
  const eventInfo = eventInfoByGti.get(group.gti);
  return {
    groupId: bulkScheduleGroupId(group.gti, group.liveStatus),
    gti: group.gti,
    titleName: group.titleName,
    liveStatus: group.liveStatus,
    assetKeys: group.assetKeys,
    currentSchedule: getCurrentSchedule(group.assetKeys, assetSchedules),
    ...(eventInfo?.predictedStartTime && {
      eventStart: eventInfo.predictedStartTime,
    }),
    ...(eventInfo?.predictedEndTime && {
      eventEnd: eventInfo.predictedEndTime,
    }),
  };
}

export function groupAssetsForBulkSchedule (
  assets: readonly AssetDto[],
  sportsCampaignScheduleMap: ReadonlyMap<string, unknown>,
  assetSchedules: ReadonlyMap<string, Schedule>,
  eventInfoByGti: ReadonlyMap<string, BulkScheduleEventInfo>,
): BulkScheduleGroup[] {
  const groups = new Map<string, MutableBulkScheduleGroup>();

  for (const asset of assets) {
    if (!isBulkScheduleEligibleAsset(asset, sportsCampaignScheduleMap)) continue;

    const title = asset.titles[0];
    const gti = title.gti!;

    const liveStatus = asset.metadata?.liveStatus ?? 'UNKNOWN';
    const groupId = bulkScheduleGroupId(gti, liveStatus);
    const existing = groups.get(groupId);
    if (existing) {
      existing.assetKeys.push(assetStrategyKey(asset));
    } else {
      groups.set(groupId, {
        gti,
        titleName: title.name ?? gti,
        liveStatus,
        assetKeys: [assetStrategyKey(asset)],
      });
    }
  }

  return Array.from(groups.values())
    .map((group) => createGroup(group, assetSchedules, eventInfoByGti))
    .sort(compareBulkScheduleGroups);
}
